import React, { useCallback, useContext, useRef, useState } from "react";
import { useHover, useUpdate } from "ahooks";

import { Context } from "../EditorContext";
import ContentType from "./ContentType";
import { useRowContext } from "../Row/RowContext";
import { useDrop } from "../hooks/useDragAndDrop";
import Tree from "../tree";
import { JSON_TPL_MAPPING } from "../Row/Row";
import { useColContext } from "./ColContext";
import classNames from 'classnames';
import DragItHere from "../Common/DragItHere";
import { useDrag } from "ahooks";
import { NO_CNT_IN_CNT } from "../NoContent/no-content";
import SelectedToolbar, { DragImg } from "../Common/SelectedToolbar";


export default ({ node, cntIndex }) => {
  const { shareRefs, notifySubscriber, removeChild, addChild, } = useContext(Context);
  const { rowShareRefs } = useRowContext();
  const { colShareRefs } = useColContext();

  const update = useUpdate();

  const { dropProps, isHovering: isDropHovering, hoverPos } = useDrop({
    onDrop: (type, data) => {
      if (shareRefs.dragToNodeType === "row") return;
      if (shareRefs.movingExist) {
        removeChild(shareRefs.movingParentNode.children, shareRefs.movingNode.id);
        node.parent.addChild(shareRefs.movingNode, hoverPos === "top" ? cntIndex : cntIndex + 1);
        shareRefs.movingExist = false;
        shareRefs.movingParentNode = null;
        shareRefs.movingNode = null;
      } else {
        let subTree;
        if (data) {
          subTree = new Tree(JSON.parse(data));
        } else {
          subTree = new Tree(JSON_TPL_MAPPING[type]);
        }
        node.parent.addChild(subTree.root, hoverPos === "top" ? cntIndex : cntIndex + 1);
      }

      shareRefs.dropedInCol = true;

      shareRefs.dragToNodeType = "";
      // remove no content block;
      const noContentBlockIndex = node.parent.children.findIndex(
        (node) => node.type === "no-content"
      );
  
      if (noContentBlockIndex > -1) {
        node.parent.children.splice(noContentBlockIndex, 1);
      }

      colShareRefs.updateCurrentCol();
    },
  });

  const delNode = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      removeChild(node.parent.children, e.currentTarget.dataset.id);
      if (node.parent.children.length === 0) {
        const subTree = new Tree(NO_CNT_IN_CNT);
        addChild(node.parent, subTree.root, 0);
      }
      shareRefs.updateRows();
      shareRefs.selectedNode = null;
      notifySubscriber("DATA_CHANGE");
      notifySubscriber("NODE_SELECT");
    },
    [removeChild, shareRefs, node.parent.children]
  );

  const duplicateNode = useCallback(
    (e: React.SyntheticEvent) => {
      const id = e.currentTarget.dataset.id;
      const foundIndex = node.parent.children.findIndex((n) => n.id === id);

      const copyJSON = node.toJSON({ isDuplcate: true });
      const subTree = new Tree(copyJSON);
      addChild(node.parent, subTree.root, foundIndex + 1);
      shareRefs.updateRows();
    },
    [node, shareRefs, addChild]
  );

  const updateSelectContent = useCallback(
    (data = {}) => {
      if (node.type === "no-content") return;
      if (data.id && node.id !== data.id) {
        node.id = data.id;
      }
      Object.assign(node.data, data);
      update();
    },
    [node, update]
  );

  const [isDraging, setIsDraging] = useState(false);

  const draggingRef = useRef<HTMLElement>(null);
  useDrag("", draggingRef, {
    onDragStart: (e) => {
      shareRefs.dragToNodeType = "col";
      shareRefs.movingExist = true;
      shareRefs.movingNode = node;
      shareRefs.movingParentNode = node.parent;
      setIsDraging(true);
    },
    onDragEnd: () => {
      setIsDraging(false);
      shareRefs.movingExist = false;
      shareRefs.movingParentNode = null;
      shareRefs.movingNode = null;
    },
    dragImage: {
      image: DragImg,
    },
  });

  const hoverRef = useRef();
  const isHovering = useHover(hoverRef, {
    onEnter: () => {
      if (node.type !== "no-content") {
        rowShareRefs.hoverInCnt = true;
        rowShareRefs.updateCurrentRow();
      }
    },
    onLeave: () => {
      rowShareRefs.hoverInCnt = false;
      rowShareRefs.updateCurrentRow();
    },
  });

  const handleClick = useCallback(
    (e) => {
      if (node.type === "no-content") return;
      e.stopPropagation();
      if (shareRefs.selectedNode) {
        shareRefs.selectedNode = node;
        // remove prev selection status;
        shareRefs.updateSelectContent();
        updateSelectContent();
        shareRefs.updateSelectContent = updateSelectContent;
      } else {
        shareRefs.selectedNode = node;
        shareRefs.updateSelectContent = updateSelectContent;
        updateSelectContent();
      }
      notifySubscriber("NODE_SELECT");
    },
    [updateSelectContent, shareRefs, notifySubscriber, node]
  );

  const onSelectParentNode = useCallback((e) => {
    e.stopPropagation();
    shareRefs.selectedNode = node.parent.parent;
    shareRefs.updateSelectContent = rowShareRefs.updateSelectRowContent;
    rowShareRefs.updateCurrentRow();
    notifySubscriber("NODE_SELECT");
  }, [node]);

  const isSelectedNode =
    shareRefs.selectedNode && node.id === shareRefs.selectedNode.id;

  return (
    <div
      {...dropProps}
      ref={hoverRef}
      style={{
        // "relative" 会阻挡 SelectedToolbar draggable 拖动事件, 会让后面列阻挡。
        position: "relative",
        outline: isSelectedNode ? "#fc679a solid 2px" : "none",
        outlineOffset: -2,
        opacity: isDraging ? 0.6 : 1,
      }}
      className={classNames({
        "drop-hover": isDropHovering && shareRefs.dragToNodeType === "col",
        "cnt-hover": isHovering && !isDropHovering && node.type !== "no-content",
      })}
      onClick={handleClick}
    >
      {React.createElement(ContentType[node.type], node)}
      <DragItHere hoverPos={hoverPos} dragHovering={isDropHovering && node.type !== "no-content" && shareRefs.dragToNodeType === "col"} />
      <SelectedToolbar
        canDuplicate
        canDelete
        onSelectParentNode={onSelectParentNode}
        isSelectedNode={isSelectedNode}
        onDuplicate={duplicateNode}
        draggingRef={draggingRef}
        id={node.id}
        onDelete={delNode}
      />
    </div>
  );
};
